import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import TopAppBar from "../../components/TopAppBar";
import DataTable from "../../components/DataTable";
import Footer from "../../components/Footer";
import PageRecords from "../../components/PageRecords";
import Pagination from "../../components/Pagination";
import DialogBox from "../../components/DialogBox";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SecurityIcon from "@mui/icons-material/Security";
import {
  addApplicationUserGrant,
  AdminFolderDto,
  ApplicationUserListItem,
  changeApplicationUserRole,
  changeApplicationUserStatus,
  FolderGrantDto,
  listAdminFolderChildren,
  listAdminFolderRoots,
  listApplicationUserGrants,
  listApplicationUsers,
  removeApplicationUserGrant,
  SortDirection,
  UserRole,
  UserSortField,
  UserStatus,
} from "../../services/userManagementService";
import * as utilFunctions from "../../utils/utilFunctions";
import { Bounce, toast } from "react-toastify";
import { antiFlashWhite, black, diamond } from "../../utils/StyleHelpers";
import { StatusWrapper } from "../ManageScreen/styled";

const NUM_ITEMS = 10;
const ALL_ROLES: UserRole[] = ["Administrator", "Manager", "User"];
const ALL_STATUSES: UserStatus[] = ["Active", "Suspended"];
const ROLE_FILTER_LABELS: Record<UserRole, string> = {
  Administrator: "Admin",
  Manager: "Manager",
  User: "User",
};
const userManagementToastOptions = {
  position: "bottom-left" as const,
  autoClose: 2000,
  hideProgressBar: false,
  closeOnClick: false,
  pauseOnHover: false,
  pauseOnFocusLoss: false,
  draggable: true,
  progress: undefined,
  theme: "colored" as const,
  transition: Bounce,
};
const checkboxStyle = {
  paddingLeft: 0,
  paddingTop: 0,
  paddingBottom: 0,
};
const getCheckBoxStyle = (isChecked: boolean) =>
  isChecked
    ? {
        background: "rgba(193, 230, 254, 1)",
        padding: "0.625rem 0.75rem",
        borderRadius: "0.5rem",
      }
    : {
        background: "#FFF",
        padding: "0.625rem 0.75rem",
        border: "1px solid #000",
        borderRadius: "0.5rem",
      };

type DialogType = "role" | "status" | "folder" | "suspend" | null;

type FolderTreeNode = AdminFolderDto & {
  children?: FolderTreeNode[];
  isExpanded?: boolean;
  isLoading?: boolean;
  error?: string;
};

const isOk = (status?: number) => status === 200 || status === 204;

const UserManagementScreen = () => {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const selfOid =
    (accounts?.[0]?.idTokenClaims as { oid?: string } | undefined)?.oid ??
    accounts?.[0]?.localAccountId;

  const [users, setUsers] = useState<ApplicationUserListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(NUM_ITEMS);
  const [roles, setRoles] = useState<UserRole[]>(ALL_ROLES);
  const [statuses, setStatuses] = useState<UserStatus[]>(ALL_STATUSES);
  const [sortField, setSortField] = useState<UserSortField>("displayName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedUser, setSelectedUser] =
    useState<ApplicationUserListItem | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogType, setDialogType] = useState<DialogType>(null);
  const [newRole, setNewRole] = useState<UserRole>("User");
  const [newStatus, setNewStatus] = useState<UserStatus>("Active");
  const [isSaving, setIsSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const closeDialog = () => {
    if (!isSaving) setDialogType(null);
  };

  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [originalGrantIds, setOriginalGrantIds] = useState<Set<string>>(
    new Set(),
  );
  const [pendingGrants, setPendingGrants] = useState<Set<string>>(new Set());
  const [pendingRevocations, setPendingRevocations] = useState<Set<string>>(
    new Set(),
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const request = useMemo(
    () => ({
      page,
      pageSize,
      roles,
      statuses,
      sort: { field: sortField, direction: sortDirection },
    }),
    [page, pageSize, roles, statuses, sortField, sortDirection],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    const response = await listApplicationUsers(request);
    setIsLoading(false);

    if (response.status === 200) {
      setUsers(response.data.users ?? []);
      setTotalCount(response.data.totalCount ?? 0);
      return;
    }

    setUsers([]);
    setTotalCount(0);
    setLoadError("Error: Encountered an error fetching users.");
    utilFunctions.handleError?.({
      status: response.response?.status ?? response.status,
      toast,
      errorMessage: `Error: Encountered an error fetching users. (CODE: ${
        response.data?.error ?? response.code
      })`,
      bounce: Bounce,
    });
  }, [request]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleRole = (role: UserRole, checked: boolean) => {
    setPage(1);
    setRoles((current) =>
      checked ? [...current, role] : current.filter((value) => value !== role),
    );
  };

  const toggleStatus = (status: UserStatus, checked: boolean) => {
    setPage(1);
    setStatuses((current) =>
      checked
        ? [...current, status]
        : current.filter((value) => value !== status),
    );
  };

  const handleSort = (field: UserSortField) => {
    setPage(1);
    if (sortField === field) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(field);
    setSortDirection("asc");
  };

  const resetFilters = () => {
    setRoles(ALL_ROLES);
    setStatuses(ALL_STATUSES);
    setSortField("displayName");
    setSortDirection("asc");
    setPage(1);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    user: ApplicationUserListItem,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleMutationDenied = (response: {
    status?: number;
    data?: { error?: string; message?: string };
  }) => {
    if (response.status === 401) {
      setDialogType(null);
      handleMenuClose();
      utilFunctions.handleError?.({ status: 401, toast });
      return true;
    }

    if (response.status === 403 && response.data?.error === "admin_required") {
      setDialogType(null);
      handleMenuClose();
      toast.error(
        response.data.message ?? "You do not have permission to manage users.",
        { ...userManagementToastOptions, autoClose: 5000 },
      );
      navigate("/main", { replace: true });
      return true;
    }

    return false;
  };

  const isSelf = (user: ApplicationUserListItem | null = selectedUser) =>
    Boolean(user && selfOid && user.objectId === selfOid);

  const roleActions = (user: ApplicationUserListItem | null) => {
    if (!user) return [];
    const actions: { label: string; role: UserRole; icon: React.ReactNode }[] =
      [];

    if (user.role !== "User") {
      actions.push({
        label: "Demote to User",
        role: "User",
        icon: <PersonIcon fontSize="small" />,
      });
    }
    if (user.role !== "Manager") {
      actions.push({
        label: "Assign Manager",
        role: "Manager",
        icon: <SecurityIcon fontSize="small" />,
      });
    }
    if (user.role !== "Administrator") {
      actions.push({
        label: "Assign Administrator",
        role: "Administrator",
        icon: <AdminPanelSettingsIcon fontSize="small" />,
      });
    }

    return actions;
  };

  const openRoleDialog = (role: UserRole) => {
    if (!selectedUser || isSelf()) return;
    setNewRole(role);
    setActionError(null);
    setDialogType("role");
    handleMenuClose();
  };

  const saveRoleChange = async () => {
    if (!selectedUser || isSelf()) return;
    setIsSaving(true);
    setActionError(null);

    const response = await changeApplicationUserRole(selectedUser.objectId, {
      role: newRole,
    });
    setIsSaving(false);
    if (handleMutationDenied(response)) return;

    if (response.status === 200) {
      toast.success(
        `User role updated to ${newRole}`,
        userManagementToastOptions,
      );
      setDialogType(null);
      fetchData();
      return;
    }

    setActionError(
      response.data?.message ?? response.data?.error ?? "Role update failed.",
    );
  };

  const openStatusDialog = () => {
    if (!selectedUser || isSelf()) return;
    setNewStatus(selectedUser.status === "Suspended" ? "Suspended" : "Active");
    setActionError(null);
    setDialogType("status");
    handleMenuClose();
  };

  const saveStatusChange = async () => {
    if (!selectedUser || isSelf()) return;
    setIsSaving(true);
    setActionError(null);

    const response = await changeApplicationUserStatus(selectedUser.objectId, {
      status: newStatus,
    });
    setIsSaving(false);
    if (handleMutationDenied(response)) return;

    if (response.status === 200) {
      toast.success(
        `User status updated to ${newStatus}`,
        userManagementToastOptions,
      );
      setDialogType(null);
      fetchData();
      return;
    }

    setActionError(
      response.data?.message ?? response.data?.error ?? "Status update failed.",
    );
  };

  const openSuspendDialog = () => {
    if (!selectedUser || isSelf()) return;
    setNewStatus(selectedUser.status === "Suspended" ? "Active" : "Suspended");
    setActionError(null);
    setDialogType("suspend");
    handleMenuClose();
  };

  const openFolderDialog = async () => {
    if (!selectedUser) return;
    setDialogType("folder");
    handleMenuClose();
    setActionError(null);
    setFolderLoading(true);
    setFolderTree([]);
    setPendingGrants(new Set());
    setPendingRevocations(new Set());

    const [grantsResponse, foldersResponse] = await Promise.all([
      listApplicationUserGrants(selectedUser.objectId),
      listAdminFolderRoots(),
    ]);

    if (grantsResponse.status === 200) {
      setOriginalGrantIds(
        new Set(
          (grantsResponse.data.grants ?? []).map(
            (grant: FolderGrantDto) => grant.folderId,
          ),
        ),
      );
    } else {
      setOriginalGrantIds(new Set());
      setActionError("Could not load existing folder grants.");
    }

    if (foldersResponse.status === 200) {
      setFolderTree(toTree(foldersResponse.data));
    } else {
      setActionError("Could not load folders.");
    }

    setFolderLoading(false);
  };

  const setNode = (
    nodes: FolderTreeNode[],
    id: string,
    updater: (node: FolderTreeNode) => FolderTreeNode,
  ): FolderTreeNode[] =>
    nodes.map((node) =>
      node.id === id
        ? updater(node)
        : {
            ...node,
            children: node.children && setNode(node.children, id, updater),
          },
    );

  const toggleExpand = async (node: FolderTreeNode) => {
    if (!node.hasChildren) return;

    if (node.children) {
      setFolderTree((current) =>
        setNode(current, node.id, (match) => ({
          ...match,
          isExpanded: !match.isExpanded,
        })),
      );
      return;
    }

    setFolderTree((current) =>
      setNode(current, node.id, (match) => ({
        ...match,
        isLoading: true,
        error: undefined,
      })),
    );

    const response = await listAdminFolderChildren(node.id);

    setFolderTree((current) =>
      setNode(current, node.id, (match) =>
        response.status === 200
          ? {
              ...match,
              children: toTree(response.data),
              isExpanded: true,
              isLoading: false,
            }
          : {
              ...match,
              isLoading: false,
              error: "Could not load child folders.",
            },
      ),
    );
  };

  const isGranted = (folderId: string) =>
    pendingGrants.has(folderId) ||
    (originalGrantIds.has(folderId) && !pendingRevocations.has(folderId));

  const toggleGrant = (folderId: string) => {
    setPendingGrants((currentGrants) => {
      const nextGrants = new Set(currentGrants);

      setPendingRevocations((currentRevocations) => {
        const nextRevocations = new Set(currentRevocations);

        if (originalGrantIds.has(folderId)) {
          if (nextRevocations.has(folderId)) {
            nextRevocations.delete(folderId);
          } else {
            nextRevocations.add(folderId);
          }
          nextGrants.delete(folderId);
        } else if (nextGrants.has(folderId)) {
          nextGrants.delete(folderId);
        } else {
          nextGrants.add(folderId);
          nextRevocations.delete(folderId);
        }

        return nextRevocations;
      });

      return nextGrants;
    });
  };

  const saveFolderAccess = async () => {
    if (!selectedUser) return;
    setIsSaving(true);
    setActionError(null);

    const failedGrants = new Set<string>();
    const failedRevocations = new Set<string>();
    const nextOriginalGrantIds = new Set(originalGrantIds);

    for (const folderId of Array.from(pendingGrants)) {
      const response = await addApplicationUserGrant(selectedUser.objectId, {
        folderId,
      });
      if (handleMutationDenied(response)) {
        setIsSaving(false);
        return;
      }
      if (isOk(response.status) || response.status === 201) {
        nextOriginalGrantIds.add(folderId);
      } else {
        failedGrants.add(folderId);
      }
    }

    for (const folderId of Array.from(pendingRevocations)) {
      const response = await removeApplicationUserGrant(
        selectedUser.objectId,
        folderId,
      );
      if (handleMutationDenied(response)) {
        setIsSaving(false);
        return;
      }
      if (isOk(response.status)) {
        nextOriginalGrantIds.delete(folderId);
      } else {
        failedRevocations.add(folderId);
      }
    }

    setIsSaving(false);
    setOriginalGrantIds(nextOriginalGrantIds);
    setPendingGrants(failedGrants);
    setPendingRevocations(failedRevocations);

    if (failedGrants.size || failedRevocations.size) {
      setActionError(
        `Some folder access changes could not be saved (${
          failedGrants.size + failedRevocations.size
        }).`,
      );
      return;
    }

    setDialogType(null);
    fetchData();
  };

  const renderSortHeader = (
    label: string,
    field: UserSortField,
    width?: string,
  ) => (
    <TableCell
      style={{ backgroundColor: antiFlashWhite, width }}
      sortDirection={sortField === field ? sortDirection : false}
      aria-sort={
        sortField === field
          ? sortDirection === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <Link
        component="button"
        onClick={() => handleSort(field)}
        aria-label={label}
        sx={{
          alignItems: "center",
          color: black,
          display: "flex",
          fontSize: "1.25rem",
          gap: 1,
          textDecoration: "none",
        }}
      >
        {label} {renderSortIcon(field)}
      </Link>
    </TableCell>
  );

  const renderSortIcon = (field: UserSortField) => {
    if (sortField !== field) {
      return <ArrowUpwardIcon sx={{ color: "rgba(0, 0, 0, 0.23)" }} />;
    }

    return sortDirection === "asc" ? (
      <ArrowUpwardIcon />
    ) : (
      <ArrowDownwardIcon />
    );
  };

  const renderStatus = (status: ApplicationUserListItem["status"]) => (
    <StatusWrapper>
      {status === "Active" ? (
        <CheckCircleOutlineIcon color="success" />
      ) : (
        <HighlightOffIcon color="error" />
      )}
      {status}
    </StatusWrapper>
  );

  const renderSelectedUserIdentity = () => (
    <Box
      sx={{
        backgroundColor: "#F0F4F8",
        p: 2,
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Typography color="text.primary">{selectedUser?.displayName}</Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", fontSize: "14px", lineHeight: "20px" }}
      >
        {selectedUser?.email} &bull; {selectedUser?.role}
      </Typography>
    </Box>
  );

  const renderGrantChips = (user: ApplicationUserListItem) => {
    const grants = user.accessibleFolders?.length
      ? user.accessibleFolders
      : user.directGrants;

    if (grants?.length) {
      return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          {grants.map((grant) => (
            <Chip
              key={grant.folderId}
              label={grant.folderName}
              size="small"
              component="button"
              clickable
              disabled={isSelf(user)}
              onClick={() => {
                setSelectedUser(user);
                openFolderDialogFor(user);
              }}
            />
          ))}
        </Stack>
      );
    }

    return user.directGrantCount > 0 ? (
      <Chip label={`${user.directGrantCount} folders`} size="small" />
    ) : (
      "-"
    );
  };

  const openFolderDialogFor = async (user: ApplicationUserListItem) => {
    setSelectedUser(user);
    setDialogType("folder");
    setActionError(null);
    setFolderLoading(true);
    setFolderTree([]);
    setPendingGrants(new Set());
    setPendingRevocations(new Set());

    const [grantsResponse, foldersResponse] = await Promise.all([
      listApplicationUserGrants(user.objectId),
      listAdminFolderRoots(),
    ]);

    setOriginalGrantIds(
      grantsResponse.status === 200
        ? new Set(
            (grantsResponse.data.grants ?? []).map(
              (grant: FolderGrantDto) => grant.folderId,
            ),
          )
        : new Set(),
    );

    if (foldersResponse.status === 200) {
      setFolderTree(toTree(foldersResponse.data));
    } else {
      setActionError("Could not load folders.");
    }

    setFolderLoading(false);
  };

  const handleTreeKeyDown = async (
    event: React.KeyboardEvent<HTMLDivElement>,
    node: FolderTreeNode,
  ) => {
    if (event.target !== event.currentTarget) return;

    const currentItem = event.currentTarget;
    const tree = currentItem.closest('[role="tree"]');
    const items = tree
      ? Array.from(tree.querySelectorAll<HTMLElement>('[role="treeitem"]'))
      : [];
    const index = items.indexOf(currentItem);
    const focusItem = (nextIndex: number) => items[nextIndex]?.focus();

    if (event.key === "ArrowDown") focusItem(index + 1);
    else if (event.key === "ArrowUp") focusItem(index - 1);
    else if (event.key === "Home") focusItem(0);
    else if (event.key === "End") focusItem(items.length - 1);
    else if (event.key === "ArrowRight" && node.hasChildren) {
      if (!node.isExpanded) await toggleExpand(node);
      window.setTimeout(() =>
        currentItem.querySelector<HTMLElement>('[role="treeitem"]')?.focus(),
      );
    } else if (event.key === "ArrowLeft") {
      if (node.isExpanded) await toggleExpand(node);
      else
        currentItem.parentElement
          ?.closest<HTMLElement>('[role="treeitem"]')
          ?.focus();
    } else return;

    event.preventDefault();
  };

  const renderFolderTree = (nodes: FolderTreeNode[], depth = 0) =>
    nodes.map((node, nodeIndex) => {
      const direct = isGranted(node.id);
      const administrator = selectedUser?.role === "Administrator";
      const checked = administrator || direct;
      const accessLabel = administrator
        ? "Administrator access"
        : direct
          ? "Direct access"
          : "No access";

      return (
        <Box
          key={node.id}
          role="treeitem"
          tabIndex={depth === 0 && nodeIndex === 0 ? 0 : -1}
          aria-label={node.name}
          aria-level={depth + 1}
          aria-expanded={
            node.hasChildren ? Boolean(node.isExpanded) : undefined
          }
          onKeyDown={(event) => handleTreeKeyDown(event, node)}
          sx={{ ml: depth * 3 }}
        >
          <Stack direction="row" alignItems="center" gap={1} py={0.5}>
            {node.hasChildren ? (
              <IconButton
                size="small"
                aria-label={`${node.isExpanded ? "Collapse" : "Expand"} ${node.name}`}
                onClick={() => toggleExpand(node)}
                disabled={node.isLoading}
              >
                {node.isLoading ? (
                  <CircularProgress size={18} />
                ) : node.isExpanded ? (
                  <ExpandMoreIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 34 }} />
            )}
            {node.isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={() => toggleGrant(node.id)}
                  inputProps={{
                    "aria-label": `Grant direct access to ${node.name}`,
                  }}
                />
              }
              label={node.name}
            />
            <Typography variant="caption">{accessLabel}</Typography>
          </Stack>
          {node.error && <Alert severity="error">{node.error}</Alert>}
          {node.isExpanded &&
            node.children &&
            renderFolderTree(node.children, depth + 1)}
        </Box>
      );
    });

  return (
    <>
      <TopAppBar nav={"manage"} manageLabel="Users" handleLogout={() => {}} />
      <MainLayout nav={"manage"}>
        <Stack
          direction="row"
          role="region"
          aria-label="User filters"
          flexWrap="wrap"
          style={{
            borderRadius: "0.5rem",
            padding: "0.25rem",
            gap: "2.5rem",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "2.5rem",
          }}
        >
          <Stack direction="row" flexWrap="wrap" gap="1rem" alignItems="center">
            <Typography fontWeight={600}>Role:</Typography>
            {ALL_ROLES.map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    checked={roles.includes(role)}
                    onChange={(event) => toggleRole(role, event.target.checked)}
                    style={checkboxStyle}
                  />
                }
                label={ROLE_FILTER_LABELS[role]}
                style={getCheckBoxStyle(roles.includes(role))}
              />
            ))}
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap="1rem" alignItems="center">
            <Typography fontWeight={600}>Status:</Typography>
            {ALL_STATUSES.map((status) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    checked={statuses.includes(status)}
                    onChange={(event) =>
                      toggleStatus(status, event.target.checked)
                    }
                    style={checkboxStyle}
                  />
                }
                label={status}
                style={getCheckBoxStyle(statuses.includes(status))}
              />
            ))}
          </Stack>
          <Button
            startIcon={<RefreshIcon />}
            variant="contained"
            onClick={fetchData}
            style={{
              borderRadius: "0.5rem",
              padding: "0.625rem 0.75rem",
              alignSelf: "flex-end",
            }}
          >
            Refresh
          </Button>
        </Stack>

        {isLoading && (
          <Stack direction="row" gap={1} alignItems="center" mb={2}>
            <CircularProgress size={20} aria-label="Loading users" />
            <Typography aria-live="polite">Loading users</Typography>
          </Stack>
        )}
        {loadError && (
          <Alert
            severity="error"
            action={<Button onClick={fetchData}>Retry</Button>}
            sx={{ mb: 2 }}
          >
            {loadError}
          </Alert>
        )}

        <DataTable
          minWidth={650}
          label="Application users"
          tableHeader={
            <>
              <TableCell style={{ backgroundColor: antiFlashWhite }}>
                #
              </TableCell>
              {renderSortHeader("Name", "displayName", "20%")}
              {renderSortHeader("Email", "email", "25%")}
              {renderSortHeader("Role", "role")}
              {renderSortHeader("Status", "status")}
              {renderSortHeader("Folder Access", "directGrantCount")}
              <TableCell
                align="center"
                style={{ backgroundColor: antiFlashWhite, width: "50px" }}
              />
            </>
          }
          tableBody={
            <TableBody>
              {isLoading && !users.length ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index} data-testid="user-row-skeleton">
                    {Array.from({ length: 7 }).map((__, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : users.length ? (
                users.map((user, index) => (
                  <TableRow
                    key={user.objectId}
                    selected={selectedUser?.objectId === user.objectId}
                    aria-selected={selectedUser?.objectId === user.objectId}
                    aria-label={`${user.displayName || ""} ${user.email || ""} ${
                      user.role
                    } ${user.status}`}
                    style={{
                      background:
                        selectedUser?.objectId === user.objectId ? diamond : "",
                    }}
                    hover
                  >
                    <TableCell component="th" scope="row">
                      {(page - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell>{user.displayName || "-"}</TableCell>
                    <TableCell>{user.email || "-"}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{renderStatus(user.status)}</TableCell>
                    <TableCell>{renderGrantChips(user)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        aria-label={`Actions for ${user.displayName || user.email}`}
                        onClick={(event) => handleMenuOpen(event, user)}
                        sx={{ color: black }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Stack alignItems="center" gap={1} py={3}>
                      <Typography>
                        No application users match these filters
                      </Typography>
                      <Button
                        startIcon={<RestartAltIcon />}
                        onClick={resetFilters}
                      >
                        Reset filters
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          }
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          MenuListProps={{ dense: true }}
          PaperProps={{
            style: { minWidth: "250px", borderRadius: "0.5rem" },
          }}
        >
          <MenuItem onClick={openStatusDialog} disabled={isSelf()}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit status</ListItemText>
          </MenuItem>
          {roleActions(selectedUser).map((action) => (
            <MenuItem
              key={action.role}
              onClick={() => openRoleDialog(action.role)}
              disabled={isSelf() || isSaving}
            >
              <ListItemIcon>{action.icon}</ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>
          ))}
          <MenuItem onClick={openFolderDialog} disabled={isSelf()}>
            <ListItemIcon>
              <FolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manage folder access</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={openSuspendDialog}
            disabled={isSelf()}
            sx={{
              color:
                selectedUser?.status === "Suspended" ? "inherit" : "#d32f2f",
            }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <BlockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {selectedUser?.status === "Suspended"
                ? "Reactivate user"
                : "Suspend user"}
            </ListItemText>
          </MenuItem>
        </Menu>

        {actionError && dialogType !== "folder" && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {actionError}
          </Alert>
        )}

        {dialogType === "role" && (
          <DialogBox
            header="Change role"
            body={
              <>
                {renderSelectedUserIdentity()}
                <Typography>
                  Change role from {selectedUser?.role} to {newRole}?
                </Typography>
                {actionError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {actionError}
                  </Alert>
                )}
              </>
            }
            subBody={null}
            onCancel={closeDialog}
            onSubmit={saveRoleChange}
            primaryButton="Confirm role change"
            secondaryButton="Cancel"
            disablePrimary={isSaving}
            isDelete={false}
            compact
          />
        )}

        {dialogType === "status" && (
          <DialogBox
            header="Edit Status"
            body={
              <>
                <Typography mb={2}>
                  Update profile details and account details
                </Typography>
                {renderSelectedUserIdentity()}
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newStatus}
                    label="Status"
                    onChange={(event) =>
                      setNewStatus(event.target.value as UserStatus)
                    }
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
                {actionError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {actionError}
                  </Alert>
                )}
              </>
            }
            subBody={null}
            onCancel={closeDialog}
            onSubmit={saveStatusChange}
            primaryButton="Update status"
            secondaryButton="Cancel"
            disablePrimary={isSaving || newStatus === selectedUser?.status}
            isDelete={false}
            compact
          />
        )}

        {dialogType === "suspend" && (
          <DialogBox
            header={newStatus === "Active" ? "Reactivate user?" : "Suspend user?"}
            body={
              <>
                {renderSelectedUserIdentity()}
                <Typography mb={2}>
                  {newStatus === "Suspended"
                    ? `${selectedUser?.displayName} will lose application access; this does not delete their record.`
                    : `${selectedUser?.displayName} will regain application access.`}
                </Typography>
                {actionError && <Alert severity="error">{actionError}</Alert>}
              </>
            }
            subBody={null}
            onCancel={closeDialog}
            onSubmit={saveStatusChange}
            primaryButton={
              newStatus === "Suspended" ? "Suspend user" : "Reactivate user"
            }
            secondaryButton="Cancel"
            disablePrimary={isSaving}
            isDelete={newStatus === "Suspended"}
            compact
          />
        )}

        {dialogType === "folder" && (
          <DialogBox
            header="Assign Folder"
            body={
              <>
                <Typography mb={2}>Assign or revoke access</Typography>
                {renderSelectedUserIdentity()}
                {actionError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {actionError}
                  </Alert>
                )}
                <Box
                  role="tree"
                  aria-label="Folder access"
                  sx={{ maxHeight: 400, overflow: "auto" }}
                >
                  {folderLoading ? (
                    <Typography aria-live="polite">Loading folders</Typography>
                  ) : folderTree.length ? (
                    renderFolderTree(folderTree)
                  ) : (
                    <Typography>No folders available</Typography>
                  )}
                </Box>
              </>
            }
            subBody={null}
            onCancel={closeDialog}
            onSubmit={saveFolderAccess}
            primaryButton="Save permissions"
            secondaryButton="Cancel"
            disablePrimary={isSaving}
            isDelete={false}
            compact
          />
        )}
      </MainLayout>
      <Footer marginLeft={"-30px"} width="calc(100% - 79px)">
        <PageRecords
          records={totalCount}
          pages={totalPages}
          selectedValue={pageSize}
          onChangePageSize={(event: React.ChangeEvent<HTMLSelectElement>) => {
            setPageSize(Number(event.target.value));
            setPage(1);
          }}
          defaultValue={NUM_ITEMS}
        />
        <Pagination
          name="Go to page"
          pages={totalPages}
          pageIndex={page}
          onChangePageIndex={(value: number) => setPage(value)}
          onChangePageRow={(
            _event: React.ChangeEvent<unknown>,
            value: number,
          ) => setPage(value)}
          activePage={page}
          marginRight=""
        />
      </Footer>
    </>
  );
};

const toTree = (
  data: AdminFolderDto[] | { folders?: AdminFolderDto[] },
): FolderTreeNode[] =>
  (Array.isArray(data) ? data : (data.folders ?? [])).map((folder) => ({
    ...folder,
    hasChildren: Boolean(folder.hasChildren),
    isExpanded: false,
    isLoading: false,
  }));

export default UserManagementScreen;
